// Hand-synchronized mirror of `tests/fixtures/rbac_matrix.yaml`.
// CI test (lib/__tests__/rbacMatrix.test.ts) parses the yaml and asserts
// shape equality, so any drift is caught at build time.
//
// Cell values:
//   "allow"    - role can perform the action unconditionally
//   "own_only" - user-role can perform only on meetings they uploaded
//                OR where their employee_id is in `participants`
//   "deny"     - role cannot perform the action

import type { Role } from "@/api/types";

export type RbacDecision = "allow" | "deny" | "own_only";

export type RbacAction =
  | "meeting_upload"
  | "meeting_list"
  | "meeting_view"
  | "meeting_edit_participants"
  | "meeting_rerun"
  | "meeting_retry_consensus"
  | "meeting_resume"
  | "client_crud"
  | "employee_crud"
  | "employee_deactivate"
  | "custom_field_crud"
  | "meeting_type_crud"
  | "prompt_edit"
  | "api_key_crud"
  | "system_settings_edit"
  | "user_invite"
  | "user_deactivate"
  | "user_change_role"
  | "user_password_reset"
  | "matching_view"
  | "matching_resolve"
  | "matching_rebuild"
  | "matching_confirm"
  | "error_view"
  | "manager_analysis_create"
  | "manager_analysis_view"
  | "analysis_template_edit"
  | "list_analysis_create"
  | "list_analysis_view";

export type RbacRow = Record<Role, RbacDecision>;

export const RBAC_MATRIX: Record<RbacAction, RbacRow> = {
  meeting_upload: { admin: "allow", manager: "allow", user: "allow" },
  meeting_list: { admin: "allow", manager: "allow", user: "allow" },
  meeting_view: { admin: "allow", manager: "allow", user: "own_only" },
  meeting_edit_participants: {
    admin: "allow",
    manager: "allow",
    user: "own_only",
  },
  meeting_rerun: { admin: "allow", manager: "allow", user: "own_only" },
  meeting_retry_consensus: {
    admin: "allow",
    manager: "allow",
    user: "own_only",
  },
  meeting_resume: { admin: "allow", manager: "allow", user: "own_only" },
  // TaskMe d3589cad: user создаёт/правит клиентов в своих папках (folder-scope
  // на бэке). Матрица зеркалит backend rbac.py + tests/fixtures/rbac_matrix.yaml.
  client_crud: { admin: "allow", manager: "allow", user: "allow" },
  employee_crud: { admin: "allow", manager: "allow", user: "deny" },
  // TM-5d6213f1: soft-delete an Employee (POST /api/employees/{id}/deactivate
  // | /restore). Admin-only — manager and user cannot reach the endpoint.
  employee_deactivate: { admin: "allow", manager: "deny", user: "deny" },
  custom_field_crud: { admin: "allow", manager: "allow", user: "deny" },
  meeting_type_crud: { admin: "allow", manager: "deny", user: "deny" },
  prompt_edit: { admin: "allow", manager: "deny", user: "deny" },
  api_key_crud: { admin: "allow", manager: "deny", user: "deny" },
  system_settings_edit: { admin: "allow", manager: "deny", user: "deny" },
  user_invite: { admin: "allow", manager: "deny", user: "deny" },
  user_deactivate: { admin: "allow", manager: "deny", user: "deny" },
  user_change_role: { admin: "allow", manager: "deny", user: "deny" },
  user_password_reset: { admin: "allow", manager: "deny", user: "deny" },
  // Phase 6 — Matching. View/resolve/confirm are gated by meeting_view
  // semantics (own_only for user). Rebuild is restricted to admin/manager.
  matching_view: { admin: "allow", manager: "allow", user: "own_only" },
  matching_resolve: { admin: "allow", manager: "allow", user: "own_only" },
  matching_rebuild: { admin: "allow", manager: "allow", user: "deny" },
  matching_confirm: { admin: "allow", manager: "allow", user: "own_only" },
  // TM-28f9536d: pipeline error registry promoted to admin-only — manager
  // and user no longer see «Ошибки» in the sidebar or load /admin/errors.
  error_view: { admin: "allow", manager: "deny", user: "deny" },
  // TM-5743cb35 / ADR-019 — Manager Analysis v2 (за FEATURE_MANAGER_ANALYSIS).
  // create/view — admin+manager. template_edit — admin-only.
  // Folder-scope для view проверяется в роутере (не в матрице).
  manager_analysis_create: { admin: "allow", manager: "allow", user: "deny" },
  manager_analysis_view: { admin: "allow", manager: "allow", user: "deny" },
  analysis_template_edit: { admin: "allow", manager: "deny", user: "deny" },
  // TM-4c1fb82b — «Анализ по списку встреч». create/view — admin+manager.
  // Folder-scope и авторство проверяются в роутере (не в матрице).
  list_analysis_create: { admin: "allow", manager: "deny", user: "deny" },
  list_analysis_view: { admin: "allow", manager: "deny", user: "deny" },
};

export const RBAC_ACTIONS = Object.keys(RBAC_MATRIX) as RbacAction[];
